import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';

import { TaskStatusTag } from 'src/shared/components';
import { Update } from 'src/shared/types/update';
import toReadableLabel from 'src/shared/utils/toReadableLabel';

import {
  ButtonV2,
  Modal,
  ModalBody,
  ModalContent
} from 'src/shared/components/ui';

import getUpdateStatus from '../../utils/getUpdateStatus';
import ProductUpdateModalContent from './ProductUpdateModalContent';

interface ProductUpdateModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  selectedUpdate: Update;
  updates: Update[];
  setSelectedUpdate: (update: Update) => void;
}

const ProductUpdateModal = ({
  isOpen,
  onOpenChange,
  selectedUpdate,
  updates,
  setSelectedUpdate
}: ProductUpdateModalProps) => {
  const renderStatusTag = () => {
    if (!selectedUpdate) {
      return null;
    }

    const type = getUpdateStatus(selectedUpdate);

    return (
      <TaskStatusTag
        className="min-w-[75px]"
        type={type}
        label={toReadableLabel(type)}
      />
    );
  };

  const onNextUpdate = () => {
    if (selectedUpdate && updates?.length) {
      const index = updates.findIndex(
        (update) => update.id === selectedUpdate.id
      );

      if (index !== -1) {
        const nextIndex = (index + 1) % updates.length;

        setSelectedUpdate(updates[nextIndex]);
      }
    }
  };

  const onPreviousUpdate = () => {
    if (selectedUpdate && updates?.length) {
      const index = updates.findIndex(
        (update) => update.id === selectedUpdate.id
      );

      if (index !== -1) {
        const prevIndex = (index - 1 + updates.length) % updates.length;

        setSelectedUpdate(updates[prevIndex]);
      }
    }
  };

  const onScrollToBottom = () => {
    const modalBody = document.querySelector('.modal-body');

    if (modalBody) {
      setTimeout(() => {
        modalBody.scrollTo({ top: modalBody.scrollHeight });
      }, 50);
    }
  };

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="3xl"
      className="min-h-[400px] h-[90%] max-w-[788px] overflow-hidden"
    >
      <ModalContent>
        {() => (
          <>
            <div className="absolute top-[50%] left-[-72px]">
              <ButtonV2 onPress={onPreviousUpdate} isIconOnly color="tertiary">
                <ArrowLeftIcon className="size-4 pointer-events-none" />
              </ButtonV2>
            </div>
            <div className="absolute top-[50%] right-[-72px]">
              <ButtonV2 onPress={onNextUpdate} isIconOnly color="tertiary">
                <ArrowRightIcon className="size-4 pointer-events-none" />
              </ButtonV2>
            </div>
            <ModalBody className="p-0 flex flex-col gap-6 relative modal-body">
              <div className="flex justify-between items-center px-6 pt-6">
                {renderStatusTag()}
              </div>
              {selectedUpdate && (
                <ProductUpdateModalContent
                  update={selectedUpdate}
                  onScrollToBottom={onScrollToBottom}
                />
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ProductUpdateModal;
